import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { Helper } from "src/app/core/helpers/helper";
import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { BusinessType } from "src/app/core/constants/bookingConstant";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { ProductVariantModalComponent } from "src/app/shared/modal-components/product-variant-modal/product-variant-modal.component";
import { ChangeMerchantModalComponent } from "src/app/shared/modal-components/change-merchant-modal/change-merchant-modal.component";
import { ProductCustomizationModalComponent } from "../../../e-delivery/components/product-customization-modal/product-customization-modal.component";
import { ProductRepeatLastCustomizationModalComponent } from "../../../e-delivery/components/product-repeat-last-customization-modal/product-repeat-last-customization-modal.component";
import { ChangeServiceModalComponent } from "../../../service/components/change-service-modal/change-service-modal.component";
import { ButtonConfig, CategoryGroup, Child, GridChildViewConfig, GridViewConfig, PriceConfig, TextConfig, Variant, Image } from "../../models/category-group.model";
import { MainCategory } from "../../models/main-category.model";
import { QCommerceService } from "../../services/q-commerce.service";
import { EDeliveryService } from "../../../e-delivery/services/e-delivery.service";
import { ServicesService } from "../../../service/services/services.service";
import { Cart } from "../../models/cart.model";
import { GroupChildType } from "src/app/core/constants/constants";
import { ClearCartModalComponent } from "src/app/shared/modal-components/clear-cart-modal/clear-cart-modal.component";

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ProductVariantModalComponent,
    TranslateModule,
    ChangeMerchantModalComponent,
    ProductCustomizationModalComponent,
    ProductRepeatLastCustomizationModalComponent,
    ChangeServiceModalComponent,
    ClearCartModalComponent
  ],
  selector: "app-product",
  templateUrl: "./product.component.html",
  styleUrl: "./product.component.scss",
  animations: [
    trigger("quantityChangeAnimation", [
      transition(":increment", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.3s ease-in", style({ opacity: 1, transform: "scale(1)" }))]),
      transition(":decrement", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.5s ease-out", style({ opacity: 1, transform: "scale(1)" }))])
    ]),
    trigger("buttonTransition", [
      transition(":enter", [style({ opacity: 0 }), animate("200ms ease-out", style({ opacity: 1 }))]),
      transition(":leave", [animate("200ms ease-in", style({ opacity: 0 }))])
    ])
  ]
})
export class ProductComponent implements OnInit {
  @Input() product!: Child;
  @Input() categoryGroup!: CategoryGroup;
  @Input() merchantId: string = "";
  @Input() serviceMainCategory!: MainCategory;
  @Input() isBrandType: boolean = false;
  @Input() serviceMerchantId: string = "";
  @Input() customConfig!: {
    isSetCustomDesign: boolean;
    gridChildViewConfig: GridChildViewConfig;
    gridViewConfig: GridViewConfig;
  };
  isDelivery!: boolean;
  isServices!: boolean;
  selectedProduct!: Child;
  isShowModal: boolean = false;
  @ViewChild("changeMerchantModal", { static: true }) changeMerchantModal!: ChangeMerchantModalComponent;
  @ViewChild("customizationModal", { static: true }) customizationModal!: ProductCustomizationModalComponent;
  @ViewChild("lastCustomizationModal", { static: true }) lastCustomizationModal!: ProductRepeatLastCustomizationModalComponent;
  @ViewChild("changeServiceModal", { static: true }) changeServiceModal!: ChangeServiceModalComponent;
  @ViewChild("clearCartModal", { static: true }) clearCartModal!: ClearCartModalComponent;

  constructor(
    public helper: Helper,
    private qcommerceService: QCommerceService,
    private edeliveryService: EDeliveryService,
    private route: ActivatedRoute,
    private servicesService: ServicesService,
    private offcanvasService: NgbOffcanvas
  ) {}

  ngOnInit(): void {
    this.isDelivery = this.helper.route.url.includes("/users/create-booking/e-delivery");
    this.isServices = this.helper.route.url.includes("/users/create-booking/services");
    this.route.queryParams.subscribe((param) => {
      if (param["merchantId"]) {
        this.merchantId = param["merchantId"];
      }
    });
  }

  /**
   * Returns the style object for the grid item.
   * The style includes flex box configuration, text alignment, background color, border,
   * border radius, and padding based on the image configuration in the grid child view
   * configuration of the category group. If the category group's child type is not
   * "CATEGORY", the background color, border, and border radius are set to an empty string.
   *
   * @param childConfig The configuration object for the grid child view.
   * @returns An object with CSS styles for the grid item
   */
  getGridItemStyle(childConfig: GridChildViewConfig): { [key: string]: string } {
    return {
      display: "flex",
      flexDirection:
        // eslint-disable-next-line no-nested-ternary
        childConfig.title.position[1] === "BOTTOM"
          ? "column"
          // eslint-disable-next-line no-nested-ternary
          : childConfig.title.position[1] === "CENTER" && childConfig.title.position[2] === "RIGHT"
          ? "row"
          : childConfig.title.position[1] === "CENTER" && childConfig.title.position[2] === "LEFT"
          ? "row-reverse"
          : "column-reverse",
      alignItems: "center",
      justifyContent: "space-between",
      textAlign: "center",
      backgroundColor: this.categoryGroup?.childType === GroupChildType.CATEGORY ? childConfig.bgColor : "",
      borderRadius:
        this.categoryGroup?.childType === GroupChildType.CATEGORY
          ? `${childConfig.border.radius[0]}px ${childConfig.border.radius[1]}px ${childConfig.border.radius[2]}px ${childConfig.border.radius[3]}px`
          : "",
      border: this.categoryGroup?.childType === GroupChildType.CATEGORY ? `${childConfig.border.size}px solid ${childConfig.border.color}` : "",
      padding: `${childConfig.padding[0]}px ${childConfig.padding[1]}px ${childConfig.padding[2]}px ${childConfig.padding[3]}px`
    };
  }

  /**
   * Returns the style object for the image.
   * The style includes padding, border, border radius, and background color based on the image configuration.
   * @param imageConfig The image configuration object.
   * @returns An object with CSS styles for the image.
   */
  getGridItemImageStyle(imageConfig: Image): { [key: string]: string | undefined } {
    return {
      padding: `${imageConfig.padding[0]}px ${imageConfig.padding[1]}px ${imageConfig.padding[2]}px ${imageConfig.padding[3]}px`,
      border: `${imageConfig.border.size}px solid ${imageConfig.border.color}`,
      borderRadius: `${imageConfig.border.radius[0]}px ${imageConfig.border.radius[1]}px ${imageConfig.border.radius[2]}px ${imageConfig.border.radius[3]}px`,
      backgroundColor: imageConfig.bgColor
    };
  }

  /**
   * Returns the style object for the title.
   * The style includes color, font size, font weight, padding, and text alignment based on
   * the title configuration.
   * @param titleConfig The title configuration object.
   * @returns An object with CSS styles for the title.
   */
  getTitleStyle(titleConfig: TextConfig): { [key: string]: string | number } {
    return {
      color: titleConfig.color,
      fontSize: `${titleConfig.fontSize}px`,
      fontWeight: titleConfig.fontWeight,
      padding: `${titleConfig.padding[0]}px ${titleConfig.padding[1]}px ${titleConfig.padding[2]}px ${titleConfig.padding[3]}px`,
      textAlign: titleConfig.position[2].toLowerCase()
    };
  }

  /**
   * Calculates the discount percentage based on the discounted and original prices.
   * If the original price is 0, it returns 0 to avoid division by zero.
   *
   * @param discountedPrice - The price after discount has been applied.
   * @param originalPrice - The original price of the product before discount.
   * @returns The discount percentage as a number.
   */
  calculateDiscountPercentage(discountedPrice: number, originalPrice: number): number {
    if (originalPrice === 0) {
      return 0;
    }
    const discount = originalPrice - discountedPrice;
    return Math.floor((discount / originalPrice) * 100);
  }

  /**
   * Increments the quantity of a product variant in the cart by 1.
   * If the product variant is not in the cart, it does not do anything.
   * @param variant The product variant to increment the quantity of.
   */
  incrementProductQuantity(variant: Variant): void {
    if (variant.status === "OUT_OF_STOCK") {
      return;
    }
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => {
      return item.productId === this.product._id && item.variantId === variant._id;
    });
    if (cartItem) {
      if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        this.edeliveryService.updateQuantity(this.product._id, variant._id, cartItem.qty + 1, variant.maxQtyAddInCart);
      } else if (this.helper.route.url.includes("/users/create-booking/services")) {
        this.servicesService.updateQuantity(this.product._id, variant._id, cartItem.qty + 1, variant.maxQtyAddInCart);
      } else {
        this.qcommerceService.updateQuantity(this.product._id, variant._id, cartItem.qty + 1, variant.maxQtyAddInCart);
      }
    }
  }

  /**
   * Decrements the quantity of a product variant in the cart by 1.
   * If the product variant is not in the cart, it does not do anything.
   * If the quantity is already 1, it removes the product variant from the cart.
   * @param variant The product variant to decrement the quantity of.
   */
  decrementProductQuantity(variant: Variant): void {
    if (variant.status === "OUT_OF_STOCK") {
      return;
    }
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => {
      return item.productId === this.product._id && item.variantId === variant._id;
    });
    if (cartItem) {
      if (cartItem.qty > 1) {
        if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
          this.edeliveryService.updateQuantity(this.product._id, variant._id, cartItem.qty - 1, variant.maxQtyAddInCart);
        } else if (this.helper.route.url.includes("/users/create-booking/services")) {
          this.servicesService.updateQuantity(this.product._id, variant._id, cartItem.qty - 1, variant.maxQtyAddInCart);
        } else {
          this.qcommerceService.updateQuantity(this.product._id, variant._id, cartItem.qty - 1, variant.maxQtyAddInCart);
        }
      } else {
        if (this.isDelivery) {
          this.edeliveryService.removeFromCart(this.product._id, variant._id);
        } else if (this.helper.route.url.includes("/users/create-booking/services")) {
          this.servicesService.removeFromCart(this.product._id, variant._id);
        } else {
          this.qcommerceService.removeFromCart(this.product._id, variant._id);
        }
      }
    }
  }

  /**
   * Gets the quantity of a product variant in the cart.
   * @param variantId The id of the product variant to get the quantity of.
   * @returns The quantity of the product variant in the cart, or 0 if it is not in the cart.
   */
  getVariantQuantity(variantId: string): number {
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => item.productId === this.product._id && item.variantId === variantId);
    return cartItem ? cartItem.qty : 0;
  }

  /**
   * Returns the total quantity of a customized product variant in the cart.
   * @param variant The product variant to get the total quantity of.
   * @returns The total quantity of the product variant in the cart.
   */
  getCustomizedProductVariantQuantity(variant: Variant): number {
    const cartItems = this.getCartItems();

    // Filter items that match the same productId and variantId
    const matchingItems = cartItems.filter((item: Cart) => item.productId === this.product._id && item.variantId === variant._id);

    // Sum up the quantities of all matching items
    return matchingItems.reduce((totalQty: number, item: Cart) => totalQty + item.qty, 0);
  }

  /**
   * Returns the total quantity of all product variants in the cart.
   * @param variants The array of product variants to calculate the total quantity of.
   * @returns The total quantity of all product variants in the cart.
   */
  totalQuantity(variants: Variant[]): number {
    return variants.reduce((total, variant) => total + this.getVariantQuantity(variant._id), 0);
  }

  totalCustomizedProductQuantity(variants: Variant[]): number {
    return variants.reduce((total, variant) => total + this.getCustomizedProductVariantQuantity(variant), 0);
  }

  /**
   * Opens the product variant modal with the given product selected.
   * @param product The product to select in the modal.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  openProductVariantsModal(product: Child): void {
    const cartItems = this.getCartItems();
    if (this.helper.route.url.includes("/users/create-booking/services") && cartItems.length > 0) {
      const cartServiceItemMainCategoryString = localStorage.getItem("cartServiceItemMainCategory");
      const cartServiceItemMainCategory = cartServiceItemMainCategoryString ? JSON.parse(cartServiceItemMainCategoryString) : "";
      if (cartItems.length && this.serviceMainCategory._id !== cartServiceItemMainCategory._id) {
        this.changeMerchantModal.openModal(this.product);
      } else if (cartItems.length > 0 && this.serviceMainCategory._id === cartServiceItemMainCategory._id && !cartServiceItemMainCategory.serviceSetting?.isAllowMultipleService) {
        if (this.selectedProduct && this.selectedProduct._id === product._id) {
          this.selectedProduct = product;
          this.isShowModal = true;
        } else {
          this.changeServiceModal.openModal(this.product);
        }
      } else {
        this.selectedProduct = product;
        this.isShowModal = true;
      }
    } else {
      this.selectedProduct = product;
      this.isShowModal = true;
    }
  }

  openRepeatLastCustomizationModal(product: Child): void {
    if (product.variants && product.variants[0] && product.variants[0].status === "OUT_OF_STOCK") {
      return;
    }
    this.lastCustomizationModal.openModal(product);
  }

  /**
   * Opens the product customization modal with the given product selected.
   * If the cart is not empty and the merchantId of the first item in the cart
   * does not match the merchantId of the current product, it opens a modal
   * to change the merchant.
   * Otherwise, it gets the product details from the eDeliveryService and
   * opens the customization modal with the product and its quantity set to 1.
   * @param product The product to select in the modal.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  openProductCustomizationModal(product: Child): void {
    if (product.variants && product.variants[0] && product.variants[0].status === "OUT_OF_STOCK") {
      return;
    }
    if (this.offcanvasService.hasOpenOffcanvas()) {
      this.offcanvasService.dismiss();
    }
    const cartItems = this.getCartItems();
    if (this.isDelivery) {
      if (cartItems.length && cartItems[0].merchantId !== this.merchantId) {
        this.changeMerchantModal.openModal(product);
      } else {
        const parameters = {
          merchantId: this.merchantId,
          businessType: BusinessType.DELIVERY
        };
        this.edeliveryService.getProductDetails(parameters, product._id).subscribe((res) => {
          if (res) {
            res.product.quantity = 1;
            this.customizationModal.openCustomizationModal(res.product, this.merchantId);
          }
        });
      }
    }
    if (this.isServices) {
      const merchantProductInCart = cartItems.find((item) => item.serviceMerchantId !== undefined);
      if (merchantProductInCart && this.serviceMerchantId && merchantProductInCart.serviceMerchantId && merchantProductInCart.serviceMerchantId !== this.serviceMerchantId) {
        this.changeMerchantModal.openModal(this.product);
        return;
      }
      if (((merchantProductInCart && !this.serviceMerchantId) || (!merchantProductInCart && this.serviceMerchantId)) && cartItems.length) {
        this.clearCartModal.openModal(this.product);
        return;
      }
      const cartServiceItemMainCategory =
        localStorage.getItem("cartServiceItemMainCategory") !== "undefined" ? JSON.parse(localStorage.getItem("cartServiceItemMainCategory") || "{}") : ({} as MainCategory);
      if (cartItems.length && this.serviceMainCategory._id !== cartServiceItemMainCategory._id) {
        this.changeMerchantModal.openModal(this.product);
      } else if (cartItems.length > 0 && this.serviceMainCategory._id === cartServiceItemMainCategory._id && !cartServiceItemMainCategory.serviceSetting?.isAllowMultipleService) {
        this.changeServiceModal.openModal(this.product);
      } else {
        const citySetting = JSON.parse(localStorage.getItem("citySetting") || "{}");
        const parameters: any = {
          cityId: citySetting.cityId ? citySetting.cityId : "",
          businessType: BusinessType.SERVICE
        };
        if (this.serviceMerchantId) {
          parameters["merchantId"] = this.serviceMerchantId;
          parameters["isForCreateOrder"] = true;
        }
        this.servicesService.getProductDetails(parameters, product._id).subscribe((res) => {
          if (res) {
            res.product.quantity = 1;
            this.customizationModal.openCustomizationModal(res.product, this.serviceMerchantId);
          }
        });
      }
    }
  }

  /**
   * Handles the modal close event from the ChangeMerchantModalComponent.
   * If a variant is provided in the event, it adds the variant to the cart.
   * Otherwise, it opens the customization modal with the provided product.
   * @param event The event emitted by the ChangeMerchantModalComponent.
   */
  onChangeMerchantModalClosed(data: { product: Child; variant?: Variant }): void {
    if (data.variant) {
      this.addProductToCart(data.variant);
    } else if (data.product && data.product.modifiers && data.product.modifiers.length > 0) {
      this.openProductCustomizationModal(data.product);
    } else {
      this.selectedProduct = data.product;
      this.isShowModal = true;
    }
  }

  /**
   * Closes the product variant modal.
   */
  closeProductVariantsModal(): void {
    this.isShowModal = false;
  }

  /**
   * Adds a product variant to the cart. If the product variant is already present in the cart,
   * the quantity will be incremented by 1, up to the maximum allowed in the cart.
   * Otherwise, the product variant will be added with an initial quantity of 1.
   *
   * @param variant - The product variant to add to the cart.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  addProductToCart(variant: Variant): void {
    if (variant.status === "OUT_OF_STOCK") {
      return;
    }
    const cartItem: Cart = {
      productId: this.product._id,
      variantId: variant._id,
      qty: 1
    };
    if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      cartItem.merchantId = this.merchantId;
    }
    if (this.serviceMerchantId) {
      cartItem.serviceMerchantId = this.serviceMerchantId;
      cartItem.isMerchantService = true;
    }
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce") || this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      cartItem.price = variant.discountedPrice || variant.price;
    }
    const cartItems = this.getCartItems();
    if (this.isDelivery) {
      if (cartItems.length && cartItems[0].merchantId !== this.merchantId) {
        this.changeMerchantModal.openModal(this.product, variant);
      } else {
        this.edeliveryService.addToCart(cartItem, variant);
      }
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      const merchantProductInCart = cartItems.find((item) => item.serviceMerchantId !== undefined);
      if (merchantProductInCart && this.serviceMerchantId && merchantProductInCart.serviceMerchantId && merchantProductInCart.serviceMerchantId !== this.serviceMerchantId) {
        this.changeMerchantModal.openModal(this.product, variant, true);
        return;
      }
      if (((merchantProductInCart && !this.serviceMerchantId) || (!merchantProductInCart && this.serviceMerchantId)) && cartItems.length) {
        this.clearCartModal.openModal(this.product, variant);
        return;
      }
      const cartServiceItemMainCategory =
        localStorage.getItem("cartServiceItemMainCategory") !== "undefined" ? JSON.parse(localStorage.getItem("cartServiceItemMainCategory") || "{}") : ({} as MainCategory);
      if (cartItems.length && this.serviceMainCategory._id !== cartServiceItemMainCategory._id) {
        this.changeMerchantModal.openModal(this.product, variant);
      } else if (cartItems.length > 0 && this.serviceMainCategory._id === cartServiceItemMainCategory._id && !cartServiceItemMainCategory.serviceSetting?.isAllowMultipleService) {
        this.changeServiceModal.openModal(this.product, variant);
      } else {
        this.servicesService.addToCart(cartItem, variant, this.serviceMainCategory);
      }
    } else {
      this.qcommerceService.addToCart(cartItem, variant);
    }
  }

  private getCartItems(): Cart[] {
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      return JSON.parse(localStorage.getItem("cartItems") || "[]");
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      return JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    }
    return JSON.parse(localStorage.getItem("deliveryCartItems") || "[]");
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  onProductClick(event: any): void {
    if (event.target.tagName === "SPAN" || event.target.tagName === "BUTTON") return;
    if (this.categoryGroup && this.categoryGroup.childType === "BRAND") {
      if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
        this.helper.route.navigate(["/users/create-booking/quick-commerce/products"], {
          queryParams: { brandId: this.product._id, name: this.product.name, merchantId: this.merchantId }
        });
      } else if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        this.helper.route.navigate(["/users/create-booking/e-delivery/product-list"], {
          queryParams: { brandId: this.product._id, name: this.product.name, merchantId: this.merchantId }
        });
      } else if (this.helper.route.url.includes("/users/create-booking/services")) {
        this.helper.route.navigate(["/users/create-booking/services/product-list"], {
          queryParams: { brandId: this.product._id, name: this.product.name }
        });
      }
    } else {
      // eslint-disable-next-line no-nested-ternary
      const gridChildViewConfig = this.categoryGroup
        ? this.categoryGroup.gridChildViewConfig
        : this.customConfig && this.customConfig.isSetCustomDesign
        ? this.customConfig.gridChildViewConfig
        : null;
      // eslint-disable-next-line no-nested-ternary
      const path = this.helper.route.url.includes("/users/create-booking/e-delivery")
        ? "/users/create-booking/e-delivery/product-details"
        : this.helper.route.url.includes("/users/create-booking/quick-commerce")
        ? "/users/create-booking/quick-commerce/product-details"
        : "/users/create-booking/services/product-details";
      let businessType;
      let merchantId;
      if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        businessType = BusinessType.DELIVERY;
        merchantId = this.merchantId;
      } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
        merchantId = this.merchantId;
        businessType = BusinessType.QUICK_COMMERCE;
      } else {
        merchantId = this.serviceMerchantId ? this.serviceMerchantId : "";
        businessType = BusinessType.SERVICE;
      }
      this.helper.route.navigate([path], {
        queryParams: {
          productId: this.product._id,
          merchantId: merchantId,
          businessType: businessType,
          serviceMainCategory: JSON.stringify(this.serviceMainCategory),
          gridChildViewConfig: JSON.stringify(gridChildViewConfig)
        }
      });
    }
  }

  getProductImage(): string {
    if (this.product.assets && this.product.assets.length > 0 && this.product.assets[0].url) {
      return this.helper.imageUrl + this.product.assets[0].url;
    } else if (this.product.variants && this.product.variants[0].assets.length > 0 && this.product.variants[0].assets[0].url) {
      return this.helper.imageUrl + this.product.variants[0].assets[0].url;
    }
    return "assets/images/default-image.jpg";
  }

  /**
   * Converts a hex color to an rgba color.
   * @param hex The hex color, with or without the '#' prefix
   * @param alpha The alpha value, from 0 to 1
   * @returns The rgba color string
   */
  hexToRgba(hex: string, alpha: number): string {
    hex = hex ? hex.replace("#", "") : "";
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Checks if a product is out of stock.
   * A product is out of stock if it has a single variant and the variant's status is "OUT_OF_STOCK".
   * @param product - The product to check.
   * @returns true if the product is out of stock, false otherwise.
   */
  isOutOfStock(product: Child): boolean {
    return product.variants && product.variants.length === 1 && product.variants[0] && product.variants[0].status === "OUT_OF_STOCK";
  }

  /**
   * Returns true if the product card title is positioned on the right side
   * of the card, false otherwise.
   * @returns true if the product card title is positioned on the right side,
   * false otherwise.
   */
  isRightLayout(): boolean {
    // eslint-disable-next-line no-nested-ternary
    return this.categoryGroup
      ? this.categoryGroup.gridChildViewConfig.title.position[1] === "RIGHT"
      : this.customConfig && this.customConfig.isSetCustomDesign
      ? this.customConfig.gridChildViewConfig.title.position[1] === "RIGHT"
      : false;
  }

  /**
   * Returns true if the product card title is positioned on the left side
   * of the card, false otherwise.
   * @returns true if the product card title is positioned on the left side,
   * false otherwise.
   */
  isLeftLayout(): boolean {
    // eslint-disable-next-line no-nested-ternary
    return this.categoryGroup
      ? this.categoryGroup.gridChildViewConfig.title.position[1] === "LEFT"
      : this.customConfig && this.customConfig.isSetCustomDesign
      ? this.customConfig.gridChildViewConfig.title.position[1] === "LEFT"
      : false;
  }

  /**
   * Returns true if the product card title is positioned at the top
   * of the card, false otherwise.
   * @returns true if the product card title is positioned at the top,
   * false otherwise.
   */
  isTopLayout(): boolean {
    // eslint-disable-next-line no-nested-ternary
    return this.categoryGroup
      ? this.categoryGroup.gridChildViewConfig.title.position[1] === "TOP"
      : this.customConfig && this.customConfig.isSetCustomDesign
      ? this.customConfig.gridChildViewConfig.title.position[1] === "TOP"
      : false;
  }

  /**
   * Returns true if the product card title is positioned at the bottom
   * of the card, false otherwise.
   * @returns true if the product card title is positioned at the bottom,
   * false otherwise.
   */
  isBottomLayout(): boolean {
    // eslint-disable-next-line no-nested-ternary
    return this.categoryGroup
      ? this.categoryGroup.gridChildViewConfig.title.position[1] === "BOTTOM"
      : this.customConfig && this.customConfig.isSetCustomDesign
      ? this.customConfig.gridChildViewConfig.title.position[1] === "BOTTOM"
      : false;
  }

  /**
   * Returns true if the product card title is positioned inside the card,
   * false otherwise.
   * @returns true if the product card title is positioned inside the card,
   * false otherwise.
   */
  isInsideLayout(): boolean {
    // eslint-disable-next-line no-nested-ternary
    return this.categoryGroup
      ? this.categoryGroup.gridChildViewConfig.title.position[0] === "INSIDE"
      : this.customConfig && this.customConfig.isSetCustomDesign
      ? this.customConfig.gridChildViewConfig.title.position[0] === "INSIDE"
      : false;
  }

  /**
   * Returns the style object for the price text.
   * The style includes font size, font weight, and color based on
   * the price configuration. If the price is the original price (before discount),
   * the color is set to the given color with 50% opacity.
   * @param priceConfig The price configuration object.
   * @param isOriginalPrice Whether the price is the original price or not.
   * @returns An object with CSS styles for the price text.
   */
  getPriceStyle(priceConfig: PriceConfig, isOriginalPrice: boolean): { [key: string]: string } {
    const priceStyle: { [key: string]: string } = {
      fontSize: `${priceConfig?.fontSize}px`,
      fontWeight: priceConfig?.fontWeight.toString()
    };
    if (isOriginalPrice) {
      priceStyle["color"] = this.hexToRgba(priceConfig?.color, 0.5);
    } else {
      priceStyle["color"] = priceConfig?.color;
    }
    return priceStyle;
  }

  /**
   * Returns the CSS style object for a button based on its configuration.
   * @param buttonConfig The button configuration object.
   * @returns An object with CSS styles for the button, including background color and color.
   */
  getButtonStyle(buttonConfig: ButtonConfig): { [key: string]: string } {
    return {
      backgroundColor: buttonConfig?.bgColor,
      color: buttonConfig?.color
    };
  }

  /**
   * Returns the CSS style object for the layout of a grid item based on its configuration.
   * If the item is inside the layout, the background color, border radius, and border
   * are set based on the configuration. Otherwise, these styles are not applied.
   * @param childConfig The configuration object for the grid child view.
   * @param isInsideLayout Whether the item is inside the layout or not.
   * @returns An object with CSS styles for the layout of the grid item.
   */
  getLayoutStyle(childConfig: GridChildViewConfig, isInsideLayout: boolean): { [key: string]: string } {
    const layoutStyle: { [key: string]: string } = {};
    if (isInsideLayout) {
      layoutStyle["background-color"] = childConfig.bgColor;
      layoutStyle["border-radius"] = `${childConfig.border.radius[0]}px ${childConfig.border.radius[1]}px ${childConfig.border.radius[2]}px ${childConfig.border.radius[3]}px`;
      layoutStyle["border"] = `${childConfig.border.size}px solid ${childConfig.border.color}`;
    }
    return layoutStyle;
  }

  /**
   * Returns the CSS style object for the product info, which includes the title,
   * price, and add to cart button. If the product info is inside the layout,
   * the padding is set based on the padding of the title configuration.
   * @param childConfig The configuration object for the grid child view.
   * @param isInsideLayout Whether the product info is inside the layout or not.
   * @returns An object with CSS styles for the product info.
   */
  getProductInfoStyle(childConfig: GridChildViewConfig, isInsideLayout: boolean): { [key: string]: string } {
    const infoStyle: { [key: string]: string } = {};
    if (isInsideLayout) {
      infoStyle["padding"] = `${childConfig.title.padding[0]}px ${childConfig.title.padding[1]}px ${childConfig.title.padding[2]}px ${childConfig.title.padding[3]}px`;
    }
    return infoStyle;
  }

  getProductCardStyle(childConfig: GridChildViewConfig, isInsideLayout: boolean): { [key: string]: string } {
    const cardStyle: { [key: string]: string } = {};
    if (isInsideLayout) {
      cardStyle["background-color"] = childConfig.bgColor;
    } else {
      cardStyle["background-color"] = childConfig.bgColor;
      cardStyle["border-radius"] = `${childConfig.border.radius[0]}px ${childConfig.border.radius[1]}px ${childConfig.border.radius[2]}px ${childConfig.border.radius[3]}px`;
      cardStyle["border"] = `${childConfig.border.size}px solid ${childConfig.border.color}`;
    }
    return cardStyle;
  }
}
